import React, { FormEvent } from 'react';
import * as moment from 'moment';
import { IListItem } from '@nkr/models/index';
import { PatientIntakeReferralStatus, ProfileRecordStatus } from '@nkr/enums/index';
import { IDetailData } from '../detail-data';
import { Checkbox, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Label, Panel } from 'react-bootstrap';
import { PanelBodyWrapper, FormField } from '../styles';
import { Controller, useFormContext } from 'react-hook-form';
import MaskedInput from 'react-maskedinput';

interface IProps {
    disableEdit: boolean,
    isAdminView: boolean,
    alertStyle: { level: number, style: string },
    previousStatus: number,
    referralStatusList: IListItem[],
    declineReasonList: IListItem[],
    availableCoordinators: IListItem[]
}

const ReferralDetails = ({ disableEdit, isAdminView, alertStyle, previousStatus, referralStatusList, declineReasonList, availableCoordinators }: IProps) => {
    const { control, setValue, watch, formState: { errors } } = useFormContext<IDetailData>();
    const referralStatusId = watch('referralStatusId');
    const recipientRecordStatus = watch('recipientRecordStatus');
    const micrositeRecordStatus = watch('micrositeRecordStatus');
    const isMicrositeCenter = watch('isMicrositeCenter');
    const createRecipient = watch('createRecipient');
    const createMicrosite = watch('createMicrosite');

    return (
        <Panel>
            <Panel.Heading>Referral Details</Panel.Heading>
            <PanelBodyWrapper>
                {!isAdminView &&
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Status:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='referralStatusId'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        name='referralStatusId'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        {referralStatusList.map(item =>
                                            <option key={'status-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                }

                {(referralStatusId == PatientIntakeReferralStatus.ClosedByCenter || referralStatusId == PatientIntakeReferralStatus.CenterDeclined) &&
                    <FormField validationState={errors.declineReason != null ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Reason for Close/Decline:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='declineReason'
                                rules={{
                                    validate: (value) => {
                                        if ((referralStatusId == PatientIntakeReferralStatus.ClosedByCenter || referralStatusId == PatientIntakeReferralStatus.CenterDeclined) && !isAdminView) {
                                            if (value == null || value == 0) {
                                                return 'Reason for closing or declining the referral is required';
                                            }

                                            return true;
                                        }
                                    }
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={null}>- Select Reason -</option>
                                        {declineReasonList.map(item => (
                                            <option key={item.id} value={item.id}>{item.description}</option>
                                        ))}
                                    </FormControl>
                                )} />

                            {errors.declineReason != null &&
                                <HelpBlock>{errors.declineReason.message}</HelpBlock>
                            }
                        </Col>
                    </FormField>
                }

                {referralStatusId == PatientIntakeReferralStatus.CenterApproved &&
                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Profile(s):</Col>
                        <Col xs={12} lg={8}>
                            {recipientRecordStatus == ProfileRecordStatus.NonExistent &&
                                <Checkbox checked={createRecipient} style={{ marginTop: 0 }} disabled={disableEdit} onClick={() => setValue('createRecipient', !createRecipient, { shouldDirty: true, shouldValidate: true })}>Create recipient profile</Checkbox>
                            }

                            {recipientRecordStatus == ProfileRecordStatus.Exists &&
                                <div style={{ marginBottom: 5 }}>
                                    <Label style={{ fontSize: '100%' }}>A recipient profile has been created</Label>
                                </div>
                            }

                            {recipientRecordStatus == ProfileRecordStatus.DifferentCenter &&
                                <div style={{ marginBottom: 5 }}>
                                    <Label style={{ fontSize: '100%' }}>A recipient profile already exists at a different center</Label>
                                </div>
                            }

                            {isMicrositeCenter &&
                                <>
                                    {micrositeRecordStatus == ProfileRecordStatus.NonExistent && (
                                        <Checkbox checked={createMicrosite} disabled={disableEdit} onClick={() => setValue('createMicrosite', !createMicrosite, { shouldDirty: true, shouldValidate: true })}>Create microsite profile</Checkbox>
                                    )}

                                    {micrositeRecordStatus == ProfileRecordStatus.Exists && (
                                        <div>
                                            <Label style={{ fontSize: '100%' }}>A microsite profile has been created</Label>
                                        </div>
                                    )}

                                    {micrositeRecordStatus == ProfileRecordStatus.DifferentCenter && (
                                        <div>
                                            <Label style={{ fontSize: '100%' }}>A microsite profile already exists at a different center...</Label>
                                        </div>
                                    )}
                                </>
                            }
                        </Col>
                    </FormGroup>
                }

                <FormField validationState={errors.coordinatorId != null ? 'error' : null}>
                    <Col xs={12} lg={4} componentClass={ControlLabel}>Coordinator:</Col>
                    <Col xs={12} lg={8}>
                        <Controller
                            control={control}
                            name='coordinatorId'
                            rules={{
                                validate: (value) => {
                                    if (value == 0 && referralStatusId != previousStatus && referralStatusId > PatientIntakeReferralStatus.PendingCenter) {
                                        return 'Coordinator is required';
                                    }

                                    return true;
                                }
                            }}
                            render={({ field: { onChange, value } }) => (
                                <FormControl
                                    componentClass='select'
                                    disabled={disableEdit}
                                    value={value || 0}
                                    onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                >
                                    <option value={0}>- Not Specified -</option>
                                    {availableCoordinators.map(item => (
                                        <option key={item.id} value={item.id}>{item.description}</option>
                                    ))}
                                </FormControl>
                            )} />

                        {errors.coordinatorId != null &&
                            <HelpBlock>{errors.coordinatorId.message}</HelpBlock>
                        }
                    </Col>
                </FormField>

                <FormField validationState={errors.followUpDate != null ? 'error' : null}>
                    <Col xs={12} lg={4} componentClass={ControlLabel}>Next Follow-Up:</Col>
                    <Col xs={12} lg={8}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>
                                <Controller
                                    control={control}
                                    name='followUpDate'
                                    rules={{
                                        validate: (value) => {
                                            if (value && !moment(value, 'MM/DD/YYYY', true).isValid()) {
                                                return 'Invalid follow-up date format';
                                            }

                                            return true;
                                        }
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <MaskedInput
                                            mask='11/11/1111'
                                            placeholder='MM/DD/YYYY'
                                            className='form-control'
                                            style={{ minWidth: 115 }}
                                            disabled={disableEdit}
                                            value={value}
                                            onChange={onChange} />
                                    )} />
                            </div>

                            {alertStyle != null &&
                                <Label bsStyle={alertStyle.style}>
                                    {alertStyle.level == 3 && (
                                        <>The next follow-up date is in the past</>
                                    )}

                                    {alertStyle.level == 2 && (
                                        <>The next follow-up date is today</>
                                    )}
                                </Label>
                            }
                        </div>

                        {errors.followUpDate != null &&
                            <HelpBlock>{errors.followUpDate.message}</HelpBlock>
                        }
                    </Col>
                </FormField>
            </PanelBodyWrapper>
        </Panel>
    )
}

export default ReferralDetails;